import { Gauge, ShieldCheck, Mail, CircleDot } from "lucide-react";

export default function Differentiators() {
  return (
    <section
      id="approach"
      data-testid="differentiators-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            §03 · The Approach
          </span>
        </div>

        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug max-w-3xl mb-12">
          Three commitments that make
          <br />
          <span className="text-zinc-500">the invisible possible.</span>
        </h2>

        {/* The Third Way — concept image */}
        <figure
          data-testid="approach-image"
          className="mb-16 fade-up"
        >
          <img
            src="/logos/third-way.webp"
            alt="Agent Speed. Engineer Oversight. — your vision, an engineer, and an experience designer aligned around the same screen."
            className="w-full h-auto block"
            loading="lazy"
          />
        </figure>

        {/* Bento grid */}
        <div className="grid grid-cols-12 gap-px bg-white/10 border border-white/10">
          {/* Card 1: Speed — large, spans 7 */}
          <div className="col-span-12 lg:col-span-7 bg-[#050505] p-8 md:p-12 group hover:bg-[#0a0a0a] transition-colors">
            <div className="flex items-start justify-between mb-8">
              <div className="flex items-center gap-3">
                <Gauge className="w-5 h-5 text-yellow-400" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
                  01 · Speed Positioning
                </span>
              </div>
              <CircleDot className="w-3 h-3 text-yellow-400" strokeWidth={1.5} />
            </div>

            <h3 className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-white mb-4">
              Near-vibe-coding speed.
              <br />
              With engineering rigor underneath.
            </h3>
            <p className="text-base leading-relaxed text-zinc-400 font-light max-w-xl">
              We sit deliberately in the middle of the spectrum — fast enough that
              you'll have something real this week, disciplined enough that it
              won't quietly rot in six months. The result is a velocity that
              compounds rather than collapses.
            </p>

            {/* Mini speedometer */}
            <div className="mt-10 border-t border-white/10 pt-6">
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">
                <span>Vibe</span>
                <span className="text-yellow-400">ISD</span>
                <span>Traditional</span>
              </div>
              <div className="relative h-1 bg-white/10">
                <div className="absolute left-[28%] -top-1 w-2 h-3 bg-yellow-400" />
              </div>
            </div>
          </div>

          {/* Card 2: HITL — spans 5 */}
          <div className="col-span-12 lg:col-span-5 bg-[#050505] p-8 md:p-12 hover:bg-[#0a0a0a] transition-colors">
            <div className="flex items-center gap-3 mb-8">
              <ShieldCheck className="w-5 h-5 text-yellow-400" strokeWidth={1.5} />
              <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
                02 · Human-in-the-Loop
              </span>
            </div>

            <h3 className="font-heading text-2xl font-light tracking-tight text-white mb-4">
              An engineer on every commit.
              <br />
              A human on every release.
            </h3>
            <p className="text-base leading-relaxed text-zinc-400 font-light mb-6">
              AI accelerates the build. Senior engineers approve it. Every change
              passes a human checkpoint before it ever touches your customers —
              with security review, uptime monitoring, and a real person on call.
            </p>

            <div className="space-y-2 border-t border-white/10 pt-5">
              {[
                "SOC2-aligned controls",
                "End-to-end encryption",
                "99.9% uptime SLA",
                "Pager-duty escalation",
              ].map((f) => (
                <div key={f} className="flex items-center gap-3 text-sm text-zinc-300 font-light">
                  <span className="w-1 h-1 bg-yellow-400" />
                  {f}
                </div>
              ))}
            </div>
          </div>

          {/* Card 3: Contemplative — full width spans 12 */}
          <div className="col-span-12 bg-[#050505] p-8 md:p-12 hover:bg-[#0a0a0a] transition-colors grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-5 h-5 text-yellow-400" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
                  03 · The Contemplative Process
                </span>
              </div>

              <h3 className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-white mb-4">
                Change requests by email.
                <br />
                <span className="italic text-zinc-400">On purpose.</span>
              </h3>
              <p className="text-base leading-relaxed text-zinc-400 font-light max-w-2xl">
                No real-time chat. No Slack pings at 11pm. Requests arrive as
                email — async, written down, considered. The friction is the
                feature: it quiets your nervous system, surfaces the request
                that actually matters, and gives us the room to design something
                that will still be right next quarter.
              </p>

              <div className="grid sm:grid-cols-3 gap-6 mt-10">
                {[
                  { k: "Calmer", v: "No always-on pings. Your team gets their attention back." },
                  { k: "Clearer", v: "Writing forces clarity. Half of requests resolve themselves." },
                  { k: "Deeper", v: "Async review surfaces second-order effects before they ship." },
                ].map((b) => (
                  <div key={b.k} className="border-t border-white/10 pt-4">
                    <div className="text-xs uppercase tracking-[0.22em] text-yellow-400 font-mono mb-2">
                      {b.k}
                    </div>
                    <div className="text-sm text-zinc-400 font-light leading-relaxed">{b.v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 flex items-center justify-center">
              <svg
                viewBox="0 0 320 220"
                className="w-full max-w-sm"
                role="img"
                aria-label="An email travels from the user to the server, then a reply travels back."
              >
                <defs>
                  {/* paths that the envelopes travel along */}
                  <path id="path-out"
                    d="M 60 84 Q 160 30 260 84" fill="none" />
                  <path id="path-in"
                    d="M 260 136 Q 160 190 60 136" fill="none" />
                </defs>

                {/* dashed guide arcs */}
                <use href="#path-out" stroke="rgba(250,204,21,0.25)" strokeWidth="1.2" strokeDasharray="3 4" />
                <use href="#path-in"  stroke="rgba(250,204,21,0.25)" strokeWidth="1.2" strokeDasharray="3 4" />

                {/* arrow heads */}
                <path d="M 256 86 l -8 -3 l 0 6 z" fill="#FACC15" />
                <path d="M 64 134 l 8 -3 l 0 6 z" fill="#FACC15" />

                {/* ───── USER (left) ───── */}
                <g transform="translate(40, 110)">
                  <circle r="22" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="1.2" />
                  <circle cx="0" cy="-5" r="6" fill="none" stroke="white" strokeWidth="1.4" />
                  <path d="M -10 11 Q 0 4 10 11" fill="none" stroke="white" strokeWidth="1.4" strokeLinecap="round" />
                  <text x="0" y="42" textAnchor="middle"
                    fontFamily="JetBrains Mono, monospace" fontSize="9"
                    letterSpacing="2" fill="rgba(255,255,255,0.55)">
                    YOU
                  </text>
                </g>

                {/* ───── SERVER (right) ───── */}
                <g transform="translate(260, 110)">
                  <rect x="-22" y="-26" width="44" height="52" rx="3" fill="none"
                    stroke="rgba(255,255,255,0.25)" strokeWidth="1.2" />
                  {[-14, -2, 10].map((y, i) => (
                    <g key={i}>
                      <rect x="-17" y={y} width="34" height="8" fill="none"
                        stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
                      <circle cx="-12" cy={y + 4} r="1.2" fill="#FACC15" />
                    </g>
                  ))}
                  <text x="0" y="46" textAnchor="middle"
                    fontFamily="JetBrains Mono, monospace" fontSize="9"
                    letterSpacing="2" fill="rgba(250,204,21,0.85)">
                    ISD
                  </text>
                </g>

                {/* ───── OUTGOING ENVELOPE (user → server) ───── */}
                <g>
                  <g transform="translate(-12,-9)" opacity="0.95">
                    <rect width="24" height="18" rx="1.5" fill="#FACC15" stroke="#050505" strokeWidth="0.8" />
                    <path d="M 0 0 L 12 10 L 24 0" fill="none" stroke="#050505" strokeWidth="1.1" strokeLinejoin="round" />
                  </g>
                  <animateMotion dur="3.6s" repeatCount="indefinite" rotate="auto"
                    keyTimes="0;0.55;1" keySplines="0.3 0 0.7 1; 0 0 1 1"
                    calcMode="spline">
                    <mpath href="#path-out" />
                  </animateMotion>
                </g>

                {/* ───── INCOMING ENVELOPE (server → user) — staggered ───── */}
                <g>
                  <g transform="translate(-12,-9)" opacity="0.95">
                    <rect width="24" height="18" rx="1.5" fill="#FACC15" stroke="#050505" strokeWidth="0.8" />
                    <path d="M 0 0 L 12 10 L 24 0" fill="none" stroke="#050505" strokeWidth="1.1" strokeLinejoin="round" />
                  </g>
                  <animateMotion dur="3.6s" begin="1.8s" repeatCount="indefinite" rotate="auto"
                    keyTimes="0;0.55;1" keySplines="0.3 0 0.7 1; 0 0 1 1"
                    calcMode="spline">
                    <mpath href="#path-in" />
                  </animateMotion>
                </g>

                {/* faint pulse at server to suggest processing */}
                <circle cx="260" cy="110" r="30" fill="none"
                  stroke="rgba(250,204,21,0.35)" strokeWidth="1"
                  className="animate-breathe" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
